import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, type PointerEvent as ReactPointerEvent, useEffect, useState } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const buttonVariants = [
  /** High-emphasis primary action (Save, Schedule, Add). */
  'primary',
  /** Filled destructive (Confirm delete). */
  'destructive',
  /** Subtle text-only destructive (Remove, Unschedule). */
  'destructive-ghost',
  /** Outline button on a white surface. */
  'secondary',
  /** Text-only neutral (Cancel). */
  'ghost',
] as const;
export type ButtonVariant = (typeof buttonVariants)[number];

export const buttonSizes = ['xs', 'sm', 'base'] as const;
export type ButtonSize = (typeof buttonSizes)[number];

interface SizeSpec {
  padding: string;
  text: string;
  iconPx: number;
  height: string;
}

const sizeSpecs: Record<ButtonSize, SizeSpec> = {
  xs: { padding: 'px-2.5 py-1', text: 'text-xs', iconPx: 10, height: 'min-h-7' },
  sm: { padding: 'px-3 py-1.5', text: 'text-xs', iconPx: 11, height: 'min-h-8' },
  base: { padding: 'px-4 py-2', text: 'text-sm', iconPx: 12, height: 'min-h-9' },
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-secondary-emphasized text-white font-medium hover:bg-secondary-focal disabled:opacity-50',
  destructive: 'bg-error-main text-white font-medium hover:bg-error-text disabled:opacity-50',
  'destructive-ghost': 'text-error-main hover:bg-error-badge disabled:opacity-50',
  secondary:
    'bg-white border border-content-border text-content-text hover:border-secondary-main hover:text-secondary-emphasized disabled:opacity-50',
  ghost: 'text-content-subtle hover:text-content-text hover:bg-content-focal disabled:opacity-50',
};

type NativeButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'children'
>;

export interface ButtonProps extends NativeButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Optional leading icon. */
  icon?: IconDefinition;
  /** When true, fills the parent's width and uses `justify-center` so the
   * label sits in the middle (e.g. the primary submit in ScheduleForm). */
  fullWidth?: boolean;
  className?: ClassName;
  children?: React.ReactNode;
}

/**
 * Standard action button. Variants cover every primary/secondary/
 * destructive pattern repeated across the app's forms, modals, and inline
 * editors. Defaults to a left-aligned label; pass `fullWidth` for the
 * spread submit-button shape used in form footers.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'sm',
      icon,
      fullWidth = false,
      className,
      type,
      children,
      ...rest
    },
    ref,
  ) => {
    const spec = sizeSpecs[size];
    // Disabled-click shake — HTML `disabled` swallows click events, so we
    // listen on a wrapping span's pointerdown. When the user pokes a
    // disabled button we set a key that re-mounts the inner button with
    // the shake animation class, then clear after the animation runs.
    const [shakeKey, setShakeKey] = useState(0);
    const isDisabled = rest.disabled === true;
    useEffect(() => {
      if (!shakeKey) return;
      const id = window.setTimeout(() => setShakeKey(0), 220);
      return () => window.clearTimeout(id);
    }, [shakeKey]);
    const handleWrapperPointerDown = (e: ReactPointerEvent<HTMLSpanElement>) => {
      if (!isDisabled) return;
      // Only trigger when the disabled button itself was poked — clicks on
      // surrounding chrome shouldn't shake the button.
      const target = e.target as HTMLElement;
      if (target.closest('button')) {
        setShakeKey((k) => k + 1);
      }
    };
    return (
      <span
        // `contents` keeps the wrapper out of the layout (the parent's
        // flex/grid sees the button directly). The span only exists to
        // receive pointerdown events on the disabled-button surface.
        className={fullWidth ? 'contents' : 'inline-flex'}
        onPointerDown={handleWrapperPointerDown}
      >
        <button
          ref={ref}
          // Remounting via key restarts the CSS animation cleanly.
          key={shakeKey}
          type={type ?? 'button'}
          className={twx(
            'inline-flex items-center gap-1.5 rounded-lg transition-[colors,transform] duration-150 disabled:cursor-not-allowed',
            // Press feedback — gentle scale-down on active. Skipped under
            // prefers-reduced-motion via the motion-safe variant.
            'motion-safe:active:scale-[0.97]',
            // Disabled-click shake — plays once per pointerdown.
            shakeKey > 0 && 'motion-safe:animate-kawaii-shake',
            spec.padding,
            spec.text,
            spec.height,
            variantClasses[variant],
            fullWidth ? 'w-full justify-center' : 'justify-start',
            className,
          )}
          {...rest}
        >
          {icon && (
            <FontAwesomeIcon
              icon={icon}
              style={{ width: spec.iconPx, height: spec.iconPx }}
              className="shrink-0"
            />
          )}
          {children}
        </button>
      </span>
    );
  },
);
Button.displayName = 'Button';
